import { API_BASE_URL } from "../config.js";
import { apiFetch } from "../platform/index.js";
import { formatOrderLink } from "../cart/orderEntries.js";
import { bindTabList, selectTab } from "../utils/ui.js";

// Named in full: nginx answers a preflight OPTIONS on the bare directory with 405 before PHP sees it.
const SEND_DESIGN_ENDPOINT = "/api/send-design/index.php";

const SEND_ERRORS = {
  invalid_email: ["designerShareEmailInvalid", "Please enter a valid email address."],
  rate_limited: ["designerShareEmailRateLimited", "Too many emails sent. Please try again later."],
  failed: ["designerShareEmailFailed", "The email couldn't be sent. Please try again."],
};

const shareTabId = (tab) => tab.dataset.shareTab;

/** Share modal: link tab, and an email tab posting to /api/send-design/ (which prices the cart itself). */
export class ShareModal {
  constructor(app, uiManager) {
    this.app = app;
    this.ui = uiManager;
    this._bound = false;
    this._tab = "link";
    this._url = null;
    this._image = null;
    this._sending = false;
  }

  get _modal() { return document.getElementById("share-modal"); }
  get _form() { return document.getElementById("share-panel-email"); }
  _tabs() { return [...(this._modal?.querySelectorAll("[role=tab][data-share-tab]") ?? [])]; }

  open(url) {
    if (!this._modal) return;
    this._bind();
    this._url = url;
    // The scene can change between opens, so the shot is taken again.
    this._image = null;
    this._setStatus("");
    this.activate(this._tab, { focus: true });
  }

  activate(tab, { focus = false } = {}) {
    const modal = this._modal;
    if (!modal) return;
    this._tab = tab;

    selectTab(this._tabs(), shareTabId, tab, focus);
    for (const panel of modal.querySelectorAll(".share-panel")) {
      panel.hidden = panel.dataset.shareTab !== tab;
    }
    if (tab === "email") this._prepareEmail();
  }

  _bind() {
    if (this._bound) return;
    this._bound = true;
    const modal = this._modal;
    bindTabList(modal.querySelector("[role=tablist]"), () => this._tabs(), shareTabId, () => this._tab, (id, opts) => this.activate(id, opts));
    // Document capture: a disabled Send drops focus to body, where EventManager reads Escape as "deselect".
    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || !modal.classList.contains("modal-visible")) return;
      e.stopPropagation();
      this.ui.closeShareModal();
    }, true);
    this._form?.addEventListener("submit", (e) => {
      e.preventDefault();
      this.send();
    });
  }

  // Shot in the task that opened the tab (a traced frame dies with it); JPEG, as WebP breaks in Outlook.
  _prepareEmail() {
    this._image ??= this.ui._captureHistoryThumbnail(undefined, undefined, "image/jpeg");
  }

  /** The request body for /api/send-design/. */
  buildPayload(email, honeypot = "") {
    const cartManager = this.app.getCartManager();
    const localization = this.app.getLocalizationManager?.();
    const country = cartManager?.countryCode;
    const order = cartManager?.hasItems() ? formatOrderLink(cartManager.getOrderEntries()) : null;

    return {
      email,
      designUrl: this._url,
      image: this._image,
      ...(order ? { order } : {}),
      lang: localization?.usingEnglish ? "en" : localization?.getLanguage() ?? "en",
      // GLOBAL is "unknown"; the endpoint then reads the country off the sender's IP.
      ...(country && country !== "GLOBAL" ? { country } : {}),
      website: honeypot,
    };
  }

  async send() {
    if (this._sending) return;
    const form = this._form;
    const input = form?.elements.email;
    const email = input?.value.trim() ?? "";

    if (!input?.checkValidity() || !email) {
      this._setStatus(this.ui.t(...SEND_ERRORS.invalid_email), "error");
      input?.focus();
      return;
    }
    if (!this._url || !this._image) {
      this._setStatus(this.ui.t(...SEND_ERRORS.failed), "error");
      return;
    }

    this._setSending(true);
    this._setStatus("");
    try {
      const response = await apiFetch(`${API_BASE_URL}${SEND_DESIGN_ENDPOINT}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(this.buildPayload(email, form.elements.website?.value ?? "")),
        timeout: 45000,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.sent) {
        this._setStatus(this.ui.t(...(SEND_ERRORS[result.error] ?? SEND_ERRORS.failed)), "error");
        return;
      }
      this._setStatus(this.ui.t("designerShareEmailSent", "Sent to {email}", { email }), "success");
    } catch (error) {
      console.warn("[share] send failed:", error);
      this._setStatus(this.ui.t(...SEND_ERRORS.failed), "error");
    } finally {
      this._setSending(false);
    }
  }

  _setSending(on) {
    this._sending = on;
    const button = document.getElementById("send-email-btn");
    if (!button) return;
    button.disabled = on;
    button.textContent = on
      ? this.ui.t("designerButtonSending", "Sending…")
      : this.ui.t("designerButtonSendEmail", "Send email");
  }

  _setStatus(text, type = "") {
    const status = document.getElementById("share-email-status");
    if (!status) return;
    status.textContent = text;
    status.dataset.type = type;
  }
}
